/** @format */

/**
 * External dependencies
 */
import moment from 'moment';
import { find } from 'lodash';
import { __ } from '@wordpress/i18n';
import { parse } from 'qs';
export var isoDateFormat = 'YYYY-MM-DD';
/**
 * DateValue Object
 *
 * @typedef  {Object} DateValue - Describes the date range supplied by the date picker.
 * @property {string} label - The translated value of the period.
 * @property {string} range - The human readable value of a date range.
 * @property {moment.Moment} after - Start of the date range.
 * @property {moment.Moment} before - End of the date range.
 */

/**
 * DateParams Object
 *
 * @typedef {Object} dateParams - date parameters derived from query parameters.
 * @property {string} period - period value, ie `last_week`
 * @property {string} compare - compare valuer, ie previous_year
 * @param {moment.Moment|null} after - If the period supplied is "custom", this is the after date
 * @param {moment.Moment|null} before - If the period supplied is "custom", this is the before date
 */

export var presetValues = [{
  value: 'today',
  label: __('Today', 'woocommerce-admin')
}, {
  value: 'yesterday',
  label: __('Yesterday', 'woocommerce-admin')
}, {
  value: 'week',
  label: __('Week to Date', 'woocommerce-admin')
}, {
  value: 'last_week',
  label: __('Last Week', 'woocommerce-admin')
}, {
  value: 'month',
  label: __('Month to Date', 'woocommerce-admin')
}, {
  value: 'last_month',
  label: __('Last Month', 'woocommerce-admin')
}, {
  value: 'quarter',
  label: __('Quarter to Date', 'woocommerce-admin')
}, {
  value: 'last_quarter',
  label: __('Last Quarter', 'woocommerce-admin')
}, {
  value: 'year',
  label: __('Year to Date', 'woocommerce-admin')
}, {
  value: 'last_year',
  label: __('Last Year', 'woocommerce-admin')
}, {
  value: 'custom',
  label: __('Custom', 'woocommerce-admin')
}];
export var periods = [{
  value: 'previous_period',
  label: __('Previous Period', 'woocommerce-admin')
}, {
  value: 'previous_year',
  label: __('Previous Year', 'woocommerce-admin')
}];
/**
 * Adds timestamp to a string date.
 *
 * @param {moment.Moment} date - Date as a moment object.
 * @param {string} timeOfDay - Either `start`, `now` or `end` of the day.
 * @return {string} - String date with timestamp attached.
 */

export var appendTimestamp = function appendTimestamp(date, timeOfDay) {
  date = date.format(isoDateFormat);

  if (timeOfDay === 'start') {
    return date + 'T00:00:00';
  }

  if (timeOfDay === 'now') {
    // Set seconds to 00 to avoid consecutives calls happening before the previous
    // one finished.
    return date + 'T' + moment().format('HH:mm:00');
  }

  if (timeOfDay === 'end') {
    return date + 'T23:59:59';
  }

  throw new Error('appendTimestamp requires second parameter to be either `start`, `now` or `end`');
};
/**
 * Convert a string to Moment object
 *
 * @param {string} format - localized date string format
 * @param {string} str - date string
 * @return {Moment|null} - Moment object representing given string
 */

export function toMoment(format, str) {
  if (moment.isMoment(str)) {
    return str.isValid() ? str : null;
  }

  if ('string' === typeof str) {
    var date = moment(str, [isoDateFormat, format], true);
    return date.isValid() ? date : null;
  }

  throw new Error('toMoment requires a string to be passed as an argument');
}
/**
 * Given two dates, derive a string representation
 *
 * @param {Moment} after - start date
 * @param {Moment} before - end date
 * @return {string} - text value for the supplied date range
 */

export function getRangeLabel(after, before) {
  var isSameYear = after.year() === before.year();
  var isSameMonth = isSameYear && after.month() === before.month();
  var isSameDay = isSameYear && isSameMonth && after.isSame(before, 'day');

  var fullDateFormat = __('MMM D, YYYY', 'woocommerce-admin');

  var monthDayFormat = __('MMM D', 'woocommerce-admin');

  if (isSameDay) {
    return after.format(fullDateFormat);
  } else if (isSameMonth) {
    var afterDate = after.date();
    return after.format(fullDateFormat).replace(afterDate, "".concat(afterDate, " - ").concat(before.date()));
  } else if (isSameYear) {
    return "".concat(after.format(monthDayFormat), " - ").concat(before.format(fullDateFormat));
  }

  return "".concat(after.format(fullDateFormat), " - ").concat(before.format(fullDateFormat));
}
/**
 * Get a DateValue object for a period prior to the current period.
 *
 * @param {string} period - the chosen period
 * @param {string} compare - `previous_period` or `previous_year`
 * @return {DateValue} -  DateValue data about the selected period
 */

export function getLastPeriod(period, compare) {
  var primaryStart = moment().startOf(period).subtract(1, period);
  var primaryEnd = primaryStart.clone().endOf(period);
  var secondaryStart;
  var secondaryEnd;

  if ('previous_period' === compare) {
    if ('year' === period) {
      // Subtract two entire periods for years to take into account leap year
      secondaryStart = moment().startOf(period).subtract(2, period);
      secondaryEnd = secondaryStart.clone().endOf(period);
    } else {
      // Otherwise, use days in primary period to figure out how far to go back
      var daysDiff = primaryEnd.diff(primaryStart, 'days');
      secondaryEnd = primaryStart.clone().subtract(1, 'days');
      secondaryStart = secondaryEnd.clone().subtract(daysDiff, 'days');
    }
  } else {
    secondaryStart = primaryStart.clone().subtract(1, 'years');
    secondaryEnd = primaryEnd.clone().subtract(1, 'years');
  }

  return {
    primaryStart: primaryStart,
    primaryEnd: primaryEnd,
    secondaryStart: secondaryStart,
    secondaryEnd: secondaryEnd
  };
}
/**
 * Get a DateValue object for a curent period. The period begins on the first day of the period,
 * and ends on the current day.
 *
 * @param {string} period - the chosen period
 * @param {string} compare - `previous_period` or `previous_year`
 * @return {DateValue} -  DateValue data about the selected period
 */

export function getCurrentPeriod(period, compare) {
  var primaryStart = moment().startOf(period);
  var primaryEnd = moment();
  var daysSoFar = primaryEnd.diff(primaryStart, 'days');
  var secondaryStart;
  var secondaryEnd;

  if ('previous_period' === compare) {
    secondaryStart = primaryStart.clone().subtract(1, period);
    secondaryEnd = primaryEnd.clone().subtract(1, period);
  } else {
    secondaryStart = primaryStart.clone().subtract(1, 'years'); // Set the end time to 23:59:59.

    secondaryEnd = secondaryStart.clone().add(daysSoFar + 1, 'days').subtract(1, 'seconds');
  }

  return {
    primaryStart: primaryStart,
    primaryEnd: primaryEnd,
    secondaryStart: secondaryStart,
    secondaryEnd: secondaryEnd
  };
}
/**
 * Get a DateValue object for a period described by a period, compare value, and start/end
 * dates, for custom dates.
 *
 * @param {string} period - the chosen period
 * @param {string} compare - `previous_period` or `previous_year`
 * @param {Moment} [after] - after date if custom period
 * @param {Moment} [before] - before date if custom period
 * @return {DateValue} - DateValue data about the selected period
 */

function getDateValue(period, compare, after, before) {
  switch (period) {
    case 'today':
      return getCurrentPeriod('day', compare);

    case 'yesterday':
      return getLastPeriod('day', compare);

    case 'week':
      return getCurrentPeriod('week', compare);

    case 'last_week':
      return getLastPeriod('week', compare);

    case 'month':
      return getCurrentPeriod('month', compare);

    case 'last_month':
      return getLastPeriod('month', compare);

    case 'quarter':
      return getCurrentPeriod('quarter', compare);

    case 'last_quarter':
      return getLastPeriod('quarter', compare);

    case 'year':
      return getCurrentPeriod('year', compare);

    case 'last_year':
      return getLastPeriod('year', compare);

    case 'custom':
      var difference = before.diff(after, 'days');

      if ('previous_period' === compare) {
        var secondaryEnd = after.clone().subtract(1, 'days');
        var secondaryStart = secondaryEnd.clone().subtract(difference, 'days');
        return {
          primaryStart: after,
          primaryEnd: before,
          secondaryStart: secondaryStart,
          secondaryEnd: secondaryEnd
        };
      }

      return {
        primaryStart: after,
        primaryEnd: before,
        secondaryStart: after.clone().subtract(1, 'years'),
        secondaryEnd: before.clone().subtract(1, 'years')
      };
  }
}
/**
 * Add default date-related parameters to a query object
 *
 * @param {Object} query - query object
 * @property {string} query.period - period value, ie `last_week`
 * @property {string} query.compare - compare value, ie `previous_year`
 * @property {string} query.after - date in iso date format, ie `2018-07-03`
 * @property {string} query.before - date in iso date format, ie `2018-07-03`
 * @param {string} defaultDateRange - the store's default date range
 * @return {DateParams} - date parameters derived from query parameters with added defaults
 */


export var getDateParamsFromQuery = function getDateParamsFromQuery(query) {
  var defaultDateRange = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'period=month&compare=previous_year';
  var period = query.period,
      compare = query.compare,
      after = query.after,
      before = query.before;

  if (period && compare) {
    return {
      period: period,
      compare: compare,
      after: after ? moment(after) : null,
      before: before ? moment(before) : null
    };
  }

  var queryDefaults = parse(defaultDateRange.replace(/&amp;/g, '&'));
  return {
    period: queryDefaults.period,
    compare: queryDefaults.compare,
    after: queryDefaults.after ? moment(queryDefaults.after) : null,
    before: queryDefaults.before ? moment(queryDefaults.before) : null
  };
};
/**
 * Get Date Value Objects for a primary and secondary date range
 *
 * @param {Object} query - query object
 * @property {string} query.period - period value, ie `last_week`
 * @property {string} query.compare - compare value, ie `previous_year`
 * @property {string} query.after - date in iso date format, ie `2018-07-03`
 * @property {string} query.before - date in iso date format, ie `2018-07-03`
 * @param {string} defaultDateRange - the store's default date range
 * @return {{primary: DateValue, secondary: DateValue}} - Primary and secondary DateValue objects
 */

export var getCurrentDates = function getCurrentDates(query) {
  var defaultDateRange = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'period=month&compare=previous_year';

  var _getDateParamsFromQue = getDateParamsFromQuery(query, defaultDateRange),
      period = _getDateParamsFromQue.period,
      compare = _getDateParamsFromQue.compare,
      after = _getDateParamsFromQue.after,
      before = _getDateParamsFromQue.before;

  var _getDateValue = getDateValue(period, compare, after, before),
      primaryStart = _getDateValue.primaryStart,
      primaryEnd = _getDateValue.primaryEnd,
      secondaryStart = _getDateValue.secondaryStart,
      secondaryEnd = _getDateValue.secondaryEnd;

  return {
    primary: {
      label: find(presetValues, function (item) {
        return item.value === period;
      }).label,
      range: getRangeLabel(primaryStart, primaryEnd),
      after: primaryStart,
      before: primaryEnd
    },
    secondary: {
      label: find(periods, function (item) {
        return item.value === compare;
      }).label,
      range: getRangeLabel(secondaryStart, secondaryEnd),
      after: secondaryStart,
      before: secondaryEnd
    }
  };
};
/**
 * Calculates the date difference between two dates. Used in calculating a matching date for previous period.
 *
 * @param {String} date - Date to compare
 * @param {String} date2 - Seconary date to compare
 * @return {Int}  - Difference in days.
 */

export var getDateDifferenceInDays = function getDateDifferenceInDays(date, date2) {
  var _date = moment(date);

  var _date2 = moment(date2);

  return _date.diff(_date2, 'days');
};
/**
 * Get the previous date for either the previous period of year.
 *
 * @param {String} date - Base date
 * @param {String|Moment.moment} date1 - primary start
 * @param {String|Moment.moment} date2 - secondary start
 * @param {String} compare - `previous_period`  or `previous_year`
 * @param {String} interval - interval
 * @return {Moment.moment}  - Calculated date
 */

export var getPreviousDate = function getPreviousDate(date, date1, date2, compare, interval) {
  var dateMoment = moment(date);

  if ('previous_year' === compare) {
    return dateMoment.clone().subtract(1, 'years');
  }

  var _date1 = moment(date1);

  var _date2 = moment(date2);

  var difference = _date1.diff(_date2, interval);

  return dateMoment.clone().subtract(difference, interval);
};
/**
 * Returns the allowed selectable intervals for a specific query.
 *
 * @param  {Object} query Current query
 * @return {Array} Array containing allowed intervals.
 */

export function getAllowedIntervalsForQuery(query) {
  var allowed = [];

  if ('custom' === query.period) {
    var _getCurrentDates = getCurrentDates(query),
        primary = _getCurrentDates.primary;

    var differenceInDays = getDateDifferenceInDays(primary.before, primary.after);

    if (differenceInDays >= 365) {
      allowed = ['day', 'week', 'month', 'quarter', 'year'];
    } else if (differenceInDays >= 90) {
      allowed = ['day', 'week', 'month', 'quarter'];
    } else if (differenceInDays >= 28) {
      allowed = ['day', 'week', 'month'];
    } else if (differenceInDays >= 7) {
      allowed = ['day', 'week'];
    } else if (differenceInDays > 1 && differenceInDays < 7) {
      allowed = ['day'];
    } else {
      allowed = ['hour', 'day'];
    }
  } else {
    switch (query.period) {
      case 'today':
      case 'yesterday':
        allowed = ['hour', 'day'];
        break;

      case 'week':
      case 'last_week':
        allowed = ['day'];
        break;

      case 'month':
      case 'last_month':
        allowed = ['day', 'week'];
        break;

      case 'quarter':
      case 'last_quarter':
        allowed = ['day', 'week', 'month'];
        break;

      case 'year':
      case 'last_year':
        allowed = ['day', 'week', 'month', 'quarter'];
        break;

      default:
        allowed = ['day'];
        break;
    }
  }

  return allowed;
}
/**
 * Returns the current interval to use.
 *
 * @param  {Object} query Current query
 * @return {String} Current interval.
 */

export function getIntervalForQuery(query) {
  var allowed = getAllowedIntervalsForQuery(query);
  var defaultInterval = allowed[0];
  var current = query.interval || defaultInterval;

  if (query.interval && !allowed.includes(query.interval)) {
    current = defaultInterval;
  }

  return current;
}
/**
 * Returns the current chart type to use.
 *
 * @param  {Object} query Current query
 * @return {String} Current chart type.
 */

export function getChartTypeForQuery(_ref) {
  var chartType = _ref.chartType;

  if (['line', 'bar'].includes(chartType)) {
    return chartType;
  }

  return 'line';
}
export var dayTicksThreshold = 63;
export var weekTicksThreshold = 9;
export var defaultTableDateFormat = 'm/d/Y';
/**
 * Returns date formats for the current interval.
 * See https://github.com/d3/d3-time-format for chart formats.
 *
 * @param  {String} interval Interval to get date formats for.
 * @param  {Int}    [ticks] Number of ticks the axis will have.
 * @return {String} Current interval.
 */

export function getDateFormatsForInterval(interval) {
  var ticks = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  var screenReaderFormat = '%B %-d, %Y';
  var tooltipLabelFormat = '%B %-d, %Y';
  var xFormat = '%Y-%m-%d';
  var x2Format = '%b %Y';
  var tableFormat = defaultTableDateFormat;

  switch (interval) {
    case 'hour':
      screenReaderFormat = '%_I%p %B %-d, %Y';
      tooltipLabelFormat = '%_I%p %b %-d, %Y';
      xFormat = '%_I%p';
      x2Format = '%b %-d, %Y';
      tableFormat = 'h A';
      break;

    case 'day':
      if (ticks < dayTicksThreshold) {
        xFormat = '%-d';
      } else {
        xFormat = '%b';
        x2Format = '%Y';
      }

      break;

    case 'week':
      if (ticks < weekTicksThreshold) {
        xFormat = '%-d';
        x2Format = '%b %Y';
      } else {
        xFormat = '%b';
        x2Format = '%Y';
      }

      screenReaderFormat = __('Week of %B %-d, %Y', 'woocommerce-admin');
      tooltipLabelFormat = __('Week of %B %-d, %Y', 'woocommerce-admin');
      break;

    case 'quarter':
    case 'month':
      screenReaderFormat = '%B %Y';
      tooltipLabelFormat = '%B %Y';
      xFormat = '%b';
      x2Format = '%Y';
      break;

    case 'year':
      screenReaderFormat = '%Y';
      tooltipLabelFormat = '%Y';
      xFormat = '%Y';
      break;
  }

  return {
    screenReaderFormat: screenReaderFormat,
    tooltipLabelFormat: tooltipLabelFormat,
    xFormat: xFormat,
    x2Format: x2Format,
    tableFormat: tableFormat
  };
}
/**
 * Gutenberg's moment instance is loaded with i18n values, which are
 * PHP date formats, ie 'LLL: "F j, Y g:i a"'. Override those with translations
 * of moment style js formats.
 *
 * @param {Object} config Locale config object, from store settings.
 */

export function loadLocaleData(_ref2) {
  var userLocale = _ref2.userLocale,
      weekdaysShort = _ref2.weekdaysShort;

  // Don't update if the wp locale hasn't been set yet, like in unit tests, for instance.
  if ('en' !== moment.locale()) {
    moment.updateLocale(userLocale, {
      longDateFormat: {
        L: __('MM/DD/YYYY', 'woocommerce-admin'),
        LL: __('MMMM D, YYYY', 'woocommerce-admin'),
        LLL: __('D MMMM YYYY LT', 'woocommerce-admin'),
        LLLL: __('dddd, D MMMM YYYY LT', 'woocommerce-admin'),
        LT: __('HH:mm', 'woocommerce-admin')
      },
      weekdaysMin: weekdaysShort
    });
  }
}
export var dateValidationMessages = {
  invalid: __('Invalid date', 'woocommerce-admin'),
  future: __('Select a date in the past', 'woocommerce-admin'),
  startAfterEnd: __('Start date must be before end date', 'woocommerce-admin'),
  endBeforeStart: __('Start date must be before end date', 'woocommerce-admin')
};
/**
 * Validate text input supplied for a date range.
 *
 * @param {string} type - Designate begining or end of range, eg `before` or `after`.
 * @param {string} value - User input value
 * @param {Moment|null} [before] - If already designated, the before date parameter
 * @param {Moment|null} [after] - If already designated, the after date parameter
 * @param {string} format - The expected date format in a user's locale
 * @return {Object} validatedDate - validated date oject
 * @param {Moment|null} validatedDate.date - A resulting Moment date object or null, if invalid
 * @param {string} validatedDate.error - An optional error message if date is invalid
 */

export function validateDateInputForRange(type, value, before, after, format) {
  var date = toMoment(format, value);

  if (!date) {
    return {
      date: null,
      error: dateValidationMessages.invalid
    };
  }

  if (moment().isBefore(date, 'day')) {
    return {
      date: null,
      error: dateValidationMessages.future
    };
  }

  if ('after' === type && before && date.isAfter(before, 'day')) {
    return {
      date: null,
      error: dateValidationMessages.startAfterEnd
    };
  }

  if ('before' === type && after && date.isBefore(after, 'day')) {
    return {
      date: null,
      error: dateValidationMessages.endBeforeStart
    };
  }

  return {
    date: date
  };
}
//# sourceMappingURL=index.js.map